import { EntityState } from "@reduxjs/toolkit";
import { Vector3D } from "common/types";
import { AssetModel, MapModel } from "common/model";
// 型のみ参照（実行時 import を避け pieceList.ts との循環を防ぐ）
import type { PieceListUiState } from "features/builder/pieceList";

// AI マップ生成データ（api-contract.md）
export interface GeneratedMapData {
  tiles: Array<{ x: number; z: number; name: string }>;
  character: { x: number; z: number };
  goal: { x: number; z: number };
  gems: Array<{ x: number; z: number }>;
  decorations: Array<{ x: number; z: number; name: string }>;
}

export type BuilderMap = {
  id: string;
  inquiry_id: string;
  author_uid: string;
  author_name: string;
  author_icon: string;
  name: string;
  version: number;
  description: string;
  capture?: string;
  cover?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
};

// サーバー /v1/pieces 一覧/詳細が返す JOIN 済みアイテム（API は status を返さない＝3軸フラグのみ）
export interface ApiPieceListItem {
  piece_id: string;
  inquiry_id: string;
  name: string;
  description: string;
  tags: string;
  capture: string | null;
  cover: string | null;
  author_uid: string;
  copied_piece_id: string | null;
  original_piece_id: string | null;
  original_author_uid: string | null;
  original_piece_created_at: string | null;
  completed: boolean;
  completed_at: string | null;
  published: boolean;
  published_at: string | null;
  deleted_at: string | null;
  banned: boolean;
  banned_at: string | null;
  enable_copied: boolean;
  enable_comment: boolean;
  is_official: boolean;
  background: string | null;
  version: number;
  liked: number;
  viewed: number;
  copied: number;
  played: number;
  cleared: number;
  commented: number;
  reaction: {
    difficult: number;
    easy: number;
    cool: number;
    fun: number;
    amazing: number;
    bad: number;
  } | null;
  created_at: string;
  updated_at: string;
  author_name: string | null;
  author_icon: string | null;
  original_author_name: string | null;
  original_author_icon: string | null;
  is_liked: boolean;
  has_draft: boolean;
  my_reactions?: string[];
}

// コメント API（GET /v1/pieces/:id/comments 等）が返す JOIN 済みアイテム
// AI 生成の当日の利用状況（サーバの実カウント）
export interface AiUsage {
  used: number;
  limit: number;
  remaining: number;
  unlimited: boolean;
}

// GET /v1/pieces/:piece_id/ranking のランキング1件
export interface PieceRankingEntry {
  rank: number;
  uid: string;
  name: string | null;
  icon: string | null;
  moves: number;
  time_ms: number | null;
}

export interface ApiPieceComment {
  id: string;
  piece_id: string;
  author_uid: string;
  parent_comment_id: string | null;
  content: string;
  reaction: {
    difficult: number;
    easy: number;
    cool: number;
    fun: number;
    amazing: number;
    bad: number;
  };
  reply_count: number;
  status: string;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  author_name: string | null;
  author_icon: string | null;
  is_author: boolean;
  my_reactions: string[];
}

// GET /v1/pieces/:piece_id/draft の編集スナップショット（メタ＋content＋command＋hint＋background）
export interface PieceDraftSnapshot {
  piece_id: string;
  completed: boolean;
  published: boolean;
  has_draft: boolean;
  name: string;
  description: string;
  tags: string;
  cover: string | null;
  capture: string | null;
  background: string | null;
  size: Vector3D;
  block_count: number;
  map: MapModel[];
  enabled_commands: string[];
  command_limits: CommandLimits | null;
  hint: string[];
  updated_at: string;
}

// マップ難易度（AI 生成）
export enum Difficulty {
  EASY = "easy",
  MEDIUM = "medium",
  HARD = "hard",
}

// ピースのライフサイクル状態（spec.md §3 状態遷移）
// 注意: API は status 文字列を返さない。真実の源はサーバーの3軸フラグで、
// この enum は表示用に derivePieceStatus でフラグから導出する派生値。
export enum PieceStatus {
  DRAFT_NEW = "draft_new",
  PUBLISHED = "published",
  EDITING = "editing",
  UNPUBLISHED = "unpublished",
  REMOVED = "removed",
}

// サーバーが返す3軸フラグ（api-contract.md「状態は API が定義しない」）
export type PieceFlags = {
  completed?: boolean;
  published?: boolean;
  has_draft?: boolean;
  deleted_at?: string | null;
  banned?: boolean;
};

// 3軸フラグ → 表示用 PieceStatus を導出。優先順は処分/削除 > 編集中 > 公開 > 完成 > 作りかけ。
export const derivePieceStatus = (p: PieceFlags): PieceStatus => {
  if (p.banned || p.deleted_at) return PieceStatus.REMOVED;
  if (p.has_draft) return PieceStatus.EDITING;
  if (p.published) return PieceStatus.PUBLISHED;
  if (p.completed) return PieceStatus.UNPUBLISHED;
  return PieceStatus.DRAFT_NEW;
};

// コマンド種別（spec.md §5.8.7）
export enum BuilderCommandId {
  MOVE_FORWARD = "move_forward",
  TURN_LEFT = "turn_left",
  TURN_RIGHT = "turn_right",
  JUMP = "jump",
  LOOP = "loop",
  IF_COLOR = "if_color",
  WHILE_COLOR = "while_color",
}

// 新規ピース作成時のデフォルト有効コマンド（spec.md §5.8.7）
export const DEFAULT_ENABLED_COMMANDS: BuilderCommandId[] = [
  BuilderCommandId.MOVE_FORWARD,
  BuilderCommandId.TURN_LEFT,
  BuilderCommandId.TURN_RIGHT,
  BuilderCommandId.JUMP,
];

// コマンド別の使用個数上限。キーがあるコマンドだけ制限（1〜）、キーなし = 無制限
export type CommandLimits = Partial<Record<BuilderCommandId, number>>;

// 完成ステップ（spec.md §5.8.3 5/5 判定）
export enum CompletionStepId {
  CHARACTER = "character",
  GOAL = "goal",
  COMMANDS = "commands",
  TESTED = "tested",
  META = "meta",
}

// AI 生成マップ形状（api-contract.md）。既存 GeneratedMapData と互換維持
export type GeneratedPieceMap = GeneratedMapData;

// 編集履歴エントリ（spec.md §5.8.4）
export interface PieceEditHistoryEntry {
  seq: number;
  version: number;
  action_type: string;
  payload?: Record<string, string | number | boolean>;
  // 復元用スナップショット（このアクション後のマップ状態）。旧データは未保持。
  map?: MapModel[];
  size?: Vector3D;
  created_at: string;
}

export enum PieceAiRole {
  USER = "user",
  ASSISTANT = "assistant",
}

// AI チャットメッセージ（api-contract.md）
export interface PieceAiMessage {
  id: string;
  session_id: string;
  role: PieceAiRole;
  content: string;
  map: GeneratedPieceMap | null;
  created_at: string;
}

// AI セッション（api-contract.md）
export interface PieceAiSession {
  session_id: string;
  piece_id: string;
  response_id?: string;
  created_at: string;
}

export type BuilderState = {
  selectedMap: BuilderMap | null;
  maps: EntityState<BuilderMap>;
  loading: boolean;
  editingBuilderMapId: string;
  size: Vector3D;
  map: EntityState<MapModel>;
  slot: AssetModel[];
  undoStacks: ActionRecord[];
  redoStacks: ActionRecord[];
  actionMenu: {
    tap: boolean;
    asset: boolean;
    eraser: boolean;
    assetList: boolean;
  };
  selectedSlotIndex: number | null;
  selectedAssetModel: AssetModel | null;
  selectedPlacedModelId: string | null;
  // ピースビルダー: 完成判定・ドラフト編集（spec.md §5.8）
  name: string;
  description: string;
  hint: string[]; // 段階別ヒント（①やさしい / ②ふつう / ③こたえ）spec.md piece_hint
  background: string | null; // 背景画像 URL（null はデフォルト背景）
  enabledCommands: BuilderCommandId[];
  commandLimits: CommandLimits; // {} = 全コマンド無制限
  tested: boolean;
  status: PieceStatus;
  editHistory: PieceEditHistoryEntry[];
  draftDirty: boolean;
  // 離脱時にシーンが生きているうちにサムネイル(capture)を撮るための要求フラグ
  captureRequested: boolean;
  // 一覧トップの左パネル UI 状態（タブ・フィルタ・並び替え・ひょうじ・スクロール）
  listUi: PieceListUiState;
  // 置けない所をタップしたときの案内。指を置いた場所に出すので画面座標を持つ
  placeHint: PlaceHint | null;
  // メッセージのリンクから「このピースの詳細を開きたい」と頼まれた内容
  pendingDetail: { pieceId: string; tab?: string } | null;
};

/** 「ここには置けない」を、子どもが見ている場所（タップした所）に出すための情報 */
export type PlaceHint = {
  messageKey: string;
  x: number;
  y: number;
  /** 同じ場所で出し直したときも再表示できるように、出すたびに増やす */
  seq: number;
};

export enum ActionCommandType {
  ADD_MODE = "ADD_MODE",
  REMOVE_MODE = "REMOVE_MODE",
  CHANGE_MODE = "CHANGE_MODE",
  // マップ全体の差し替え（AI生成の適用）。もどす/やりなおすで元に戻せるようにする
  REPLACE_ALL = "REPLACE_ALL",
  UNDO = "UNDO",
  REDO = "REDO",
  CLEAR = "CLEAR",
}

// Undo/Redo用のアクションペイロード
export interface ActionPayload {
  model?: MapModel;
  models?: MapModel[];
  meshId?: string;
  id?: string;
  changes?: { [key: string]: string | Vector3D };
  // restore の差し替えは大きさも戻す
  size?: Vector3D;
}

// アクション履歴レコード
export interface ActionRecord {
  type: ActionCommandType;
  forward: ActionPayload;
  backward: ActionPayload;
  timestamp: number;
  // REPLACE_ALL の中身（ai_generate / restore）。undo / redo を記録に残すときに、何を取り消したかを書く
  label?: string;
}

/** 記録の区切りの種類（docs/edit-history-undo.md）。完成・公開・公開をやめる・すてる は API が書く */
export enum PieceMilestone {
  OPEN = "open",
  CLOSE = "close",
  COMPLETE = "complete",
  PUBLISH = "publish",
  UNPUBLISH = "unpublish",
  RESTORE = "restore",
  DISCARD = "discard",
}

/** 記録を見られる範囲（未課金は新しい区切り 10 個分。-1 は全部） */
export type PieceEditHistoryResponse = {
  entries: PieceEditHistoryEntry[];
  limit: number;
  total_milestones: number;
};

export interface AddModeCommand {
  type: ActionCommandType.ADD_MODE;
  model: MapModel;
}

export interface RemoveModeCommand {
  type: ActionCommandType.REMOVE_MODE;
  meshId: string;
}

export interface ChangeModeCommand {
  type: ActionCommandType.CHANGE_MODE;
  id: string;
  changes: {
    [item: string]: string | Vector3D;
  };
}

export interface UndoCommand {
  type: ActionCommandType.UNDO;
}

export interface RedoCommand {
  type: ActionCommandType.REDO;
}

export interface ClearCommand {
  type: ActionCommandType.CLEAR;
}

export type ActionCommand =
  | AddModeCommand
  | RemoveModeCommand
  | ChangeModeCommand
  | UndoCommand
  | RedoCommand
  | ClearCommand;
